import { useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import {
  Avatar,
  Badge,
  Box,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  styled,
  Tooltip,
  Typography,
} from '@mui/material';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { useStateParam } from 'features/Connect/features/Context/ConnectStateCodeContextProvider';
import { useConnectContext } from 'features/Connect/features/Context/ConnectContextProvider';
import { candidatePortalRoutes, nimbleConnectUrlFlag } from 'utils/constants/urls';
import { getConnectUrlPath } from 'features/Connect/features/JobDiscovery/utils';
import { handleCloseUserMenu, handleOpenUserMenu } from './utils';
import { candidateMenuOptions } from './constants';
import {
  CandidateNavbarUserDropdownMenuProps,
  NotificationData,
  NotificationParams,
} from '../types';
import { CandidateNavbarDataTestIds } from 'data-testids/general';
import auth from 'utils/auth';

export const CandidateNavbarUserDropdownMenu: React.FC<CandidateNavbarUserDropdownMenuProps> = ({
  user,
  candidateMatches,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const history = useHistory();
  const isOnConnect = useRouteMatch({ path: `/${nimbleConnectUrlFlag}` });
  const jobBoardState = useStateParam();
  const connectUrlPath = getConnectUrlPath(jobBoardState);
  const { connectContextActions } = useConnectContext();
  const atMatchDashboard = useRouteMatch({ path: candidatePortalRoutes.MATCH_DASHBOARD });
  const hasUserInfo = user?.first_name && user?.last_name && user?.email;

  const mainMenuItems = candidateMenuOptions.filter((option) => option.menuSection === 'main');
  const footerMenuItems = candidateMenuOptions.filter((option) => option.menuSection === 'footer');
  const MenuActionParams = {
    isOnConnect: !!isOnConnect,
    userType: user.profile.user_type,
    jobBoardState,
    connectContextActions,
    history,
    connectUrlPath,
  };

  const notificationParams: NotificationParams = {
    atMatchDashboard: !!atMatchDashboard,
    isOnConnect: !!isOnConnect,
  };

  const getBadgeContent = () => {
    let totalCount = 0;

    // Check all menu items for notifications
    [...mainMenuItems, ...footerMenuItems].forEach((option) => {
      if (option.notification) {
        const { config } = option.notification;
        const notificationData: NotificationData = {
          candidateMatches,
        };
        const count = config.getData(notificationData);
        const shouldShow = config.shouldShow(notificationParams);
        if (shouldShow && count > 0) {
          totalCount += count;
        }
      }
    });

    return totalCount;
  };

  const renderMenuLabel = (option: (typeof candidateMenuOptions)[0]): React.ReactNode => {
    if (typeof option.label === 'function' && option.notification) {
      const { config } = option.notification;
      const notificationData: NotificationData = {
        candidateMatches,
      };
      const count = config.getData(notificationData);
      const shouldShow = config.shouldShow(notificationParams);
      return option.label({
        count,
        shouldShowNotification: shouldShow && count > 0,
        context: 'navbar',
      });
    }
    return option.label as React.ReactNode;
  };

  return (
    <Box sx={{ flexGrow: 0 }}>
      <Tooltip title="Open settings">
        <IconButton
          onClick={(e) => handleOpenUserMenu(e, setAnchorEl)}
          sx={{ p: 0 }}
          data-testid={CandidateNavbarDataTestIds.USER_MENU}
        >
          <Badge
            badgeContent={getBadgeContent()}
            color="primary"
            overlap="circular"
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            invisible={!getBadgeContent()}
            data-testid={CandidateNavbarDataTestIds.NOTIFICATION_BADGE}
          >
            <Avatar alt={user?.first_name} src="placeholder" />
          </Badge>
        </IconButton>
      </Tooltip>
      <StyledMenu
        id="menu-appbar"
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(anchorEl)}
        onClose={() => handleCloseUserMenu(setAnchorEl)}
      >
        {hasUserInfo && (
          <>
            <UserInfoSection>
              <UserName data-testid="user-menu-name">
                {user.first_name} {user.last_name}
              </UserName>
              <UserEmail data-testid="user-menu-email">{user.email}</UserEmail>
            </UserInfoSection>
            <DividerWrapper>
              <Divider />
            </DividerWrapper>
          </>
        )}
        {footerMenuItems.map((option) => (
          <MenuItem
            key={option.key}
            onClick={() => {
              handleCloseUserMenu(setAnchorEl);
              option.menuItemAction(MenuActionParams);
            }}
            data-testid={option.dataTestId}
          >
            {option.icon && <IconWrapper>{option.icon}</IconWrapper>}
            <MenuItemText data-testid={`${option.dataTestId}-label`}>
              {renderMenuLabel(option)}
            </MenuItemText>
          </MenuItem>
        ))}
        {user.is_hijacked && (
          <MenuItem
            data-testid={CandidateNavbarDataTestIds.USER_MENU_RELEASE_HIJACK}
            onClick={() => {
              handleCloseUserMenu(setAnchorEl);
              auth.handleReleaseHijack();
            }}
          >
            <IconWrapper>
              <LockOpenIcon />
            </IconWrapper>
            <MenuItemText
              data-testid={`${CandidateNavbarDataTestIds.USER_MENU_RELEASE_HIJACK}-label`}
            >
              Release Hijack
            </MenuItemText>
          </MenuItem>
        )}
      </StyledMenu>
    </Box>
  );
};

const StyledMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    minWidth: '200px',
    marginTop: theme.spacing(5.5),
  },
}));

const UserInfoSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0.5, 2, 0.5),
}));

const UserName = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: theme.typography.subtitle.fontSize,
  fontWeight: theme.typography.fontWeightRegular,
  lineHeight: '150%',
  letterSpacing: '0.15px',
}));

const UserEmail = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: theme.typography.body2.fontSize,
  fontWeight: theme.typography.fontWeightRegular,
  lineHeight: '143%',
  letterSpacing: '0.17px',
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  marginRight: theme.spacing(1),
  display: 'flex',
  alignItems: 'center',
  color: '#707070',
}));

const DividerWrapper = styled(Box)(({ theme }) => ({
  margin: theme.spacing(1),
}));

const MenuItemText = styled('span')(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  color: theme.palette.text.primary,
  fontSize: theme.typography.subtitle.fontSize,
  fontStyle: 'normal',
  fontWeight: theme.typography.fontWeightRegular,
}));
